import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { exportGuideToPDF } from '@/lib/pdfExport';
import { 
  ArrowRight, ArrowLeft, Building2, Users, CalendarDays, 
  Bell, CheckCircle, Clock, Download, BookOpen, Loader2
} from 'lucide-react';
import BrandLogo from '../../components/BrandLogo';

const steps = [
  {
    id: 1,
    title: "Welcome to ExpressSub!",
    subtitle: "Let's get you set up in just a few minutes",
    icon: Clock,
    content: (
      <div className="space-y-4">
        <p className="text-slate-600">
          ExpressSub helps you manage substitute teachers efficiently. You'll be able to:
        </p>
        <ul className="space-y-3">
          <li className="flex items-center gap-3">
            <div className="h-8 w-8 bg-emerald-100 rounded-full flex items-center justify-center">
              <Users className="h-4 w-4 text-emerald-600" />
            </div>
            <span>Build a pool of trusted substitute teachers</span>
          </li>
          <li className="flex items-center gap-3">
            <div className="h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center">
              <CalendarDays className="h-4 w-4 text-blue-600" />
            </div>
            <span>Post positions and get instant responses</span>
          </li>
          <li className="flex items-center gap-3">
            <div className="h-8 w-8 bg-amber-100 rounded-full flex items-center justify-center">
              <Bell className="h-4 w-4 text-amber-600" />
            </div>
            <span>Track responses and fill vacancies quickly</span>
          </li>
        </ul>
      </div>
    )
  },
  {
    id: 2,
    title: "Your School Code",
    subtitle: "Share this with your trusted substitutes",
    icon: Building2,
    content: (
      <div className="space-y-4">
        <p className="text-slate-600">
          Your school has a unique code that substitutes use to connect with you.
        </p>
        <div className="bg-slate-100 rounded-lg p-6 text-center">
          <p className="text-sm text-slate-500 mb-2">Your code will appear on your dashboard</p>
          <div className="text-3xl font-mono font-bold text-slate-800 tracking-wider">
            ● ● ● ● ● ● ● ●
          </div>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <p className="text-sm text-amber-800">
            <strong>Important:</strong> Only share your code with substitutes you trust. 
            When they enter it, you'll receive a request to approve them.
          </p>
        </div>
      </div>
    )
  },
  {
    id: 3,
    title: "Approving Substitutes",
    subtitle: "You control who joins your list",
    icon: Users,
    content: (
      <div className="space-y-4">
        <p className="text-slate-600">
          When a substitute enters your school code:
        </p>
        <ol className="space-y-4">
          <li className="flex items-start gap-3">
            <div className="h-6 w-6 bg-emerald-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">1</div>
            <span>You'll see their request in the "Pending Requests" section on your dashboard</span>
          </li>
          <li className="flex items-start gap-3">
            <div className="h-6 w-6 bg-emerald-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">2</div>
            <span>Review their information and click <strong>Approve</strong> or <strong>Decline</strong></span>
          </li>
          <li className="flex items-start gap-3">
            <div className="h-6 w-6 bg-emerald-600 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">3</div>
            <span>Approved substitutes will appear on your selection list for position postings</span>
          </li>
        </ol>
      </div>
    )
  },
  {
    id: 4,
    title: "Posting Positions",
    subtitle: "Fill vacancies fast with targeted posts",
    icon: CalendarDays,
    content: (
      <div className="space-y-4">
        <p className="text-slate-600">
          Create position postings with just a few clicks:
        </p>
        <ul className="space-y-3">
          <li className="flex items-start gap-2">
            <CheckCircle className="h-5 w-5 text-emerald-600 mt-0.5 flex-shrink-0" />
            <span><strong>Select dates:</strong> Pick one or multiple dates from the calendar</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle className="h-5 w-5 text-emerald-600 mt-0.5 flex-shrink-0" />
            <span><strong>Set times:</strong> Use your school's default times or customize</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle className="h-5 w-5 text-emerald-600 mt-0.5 flex-shrink-0" />
            <span><strong>Choose category:</strong> Specify the job category defined in your school settings</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle className="h-5 w-5 text-emerald-600 mt-0.5 flex-shrink-0" />
            <span><strong>Target specific subs:</strong> Send to all or select individuals</span>
          </li>
        </ul>
        <p className="text-sm text-slate-500 mt-4">
          Substitutes receive instant notifications and can respond immediately!
        </p>
      </div>
    )
  },
  {
    id: 5,
    title: "Tracking Responses",
    subtitle: "Monitor views and responses in real-time",
    icon: Bell,
    content: (
      <div className="space-y-4">
        <p className="text-slate-600">
          Your dashboard shows you exactly what's happening with each position:
        </p>
        <div className="space-y-3">
          <div className="bg-slate-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="h-2 w-2 bg-blue-500 rounded-full"></div>
              <span className="font-medium">View Tracking</span>
            </div>
            <p className="text-sm text-slate-600">See "X of Y viewed" to know who has seen your posting</p>
          </div>
          <div className="bg-slate-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="h-2 w-2 bg-emerald-500 rounded-full"></div>
              <span className="font-medium">Response Status</span>
            </div>
            <p className="text-sm text-slate-600">Track accepted, declined, and pending responses</p>
          </div>
          <div className="bg-slate-50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="h-2 w-2 bg-amber-500 rounded-full"></div>
              <span className="font-medium">Auto Reminders</span>
            </div>
            <p className="text-sm text-slate-600">Set frequency for system reminders sent to non-responders</p>
          </div>
        </div>
      </div>
    )
  },
  {
    id: 6,
    title: "You're Ready!",
    subtitle: "Start building your substitute network",
    icon: CheckCircle,
    content: (
      <div className="space-y-4">
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-6 text-center">
          <CheckCircle className="h-12 w-12 text-emerald-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-emerald-800 mb-2">Setup Complete!</h3>
          <p className="text-emerald-700">You're ready to start using ExpressSub</p>
        </div>
        <div className="space-y-2">
          <p className="font-medium text-slate-700">Quick Start Checklist:</p>
          <ul className="space-y-2">
            <li className="flex items-center gap-2 text-sm">
              <CheckCircle className="h-4 w-4 text-emerald-600" />
              Share your school code with trusted substitutes
            </li>
            <li className="flex items-center gap-2 text-sm">
              <CheckCircle className="h-4 w-4 text-emerald-600" />
              Set your school's default times in Settings
            </li>
            <li className="flex items-center gap-2 text-sm">
              <CheckCircle className="h-4 w-4 text-emerald-600" />
              Post your first position when you need coverage
            </li>
          </ul>
        </div>
      </div>
    )
  }
];

export default function AdminOnboarding() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [isExporting, setIsExporting] = useState(false);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // Mark onboarding as complete
      localStorage.setItem('admin_onboarding_complete', 'true');
      navigate('/employer');
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    localStorage.setItem('admin_onboarding_complete', 'true');
    navigate('/employer');
  };

  const handleDownloadPDF = async () => {
    setIsExporting(true);
    try {
      const success = await exportGuideToPDF('admin-onboarding-print', 'ExpressSub-Admin-Onboarding-Guide', 'Admin Onboarding');
      if (success) {
        toast.success('PDF downloaded successfully!');
      } else {
        toast.error('Failed to generate PDF');
      }
    } catch (error) {
      toast.error('Failed to generate PDF');
    } finally {
      setIsExporting(false);
    }
  };

  const progress = ((currentStep + 1) / steps.length) * 100;
  const step = steps[currentStep];
  const StepIcon = step.icon;

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-slate-100 flex items-center justify-center p-4">
      {/* Hidden Print View for PDF - Shows all steps */}
      <div id="admin-onboarding-print" style={{ position: 'absolute', left: '-9999px', width: '800px', padding: '40px', background: 'white' }}>
        <div className="flex items-center gap-3 mb-6">
          <BrandLogo variant="black" size="xl" />
        </div>
        <h1 className="text-2xl font-bold mb-2">School Administrator Onboarding Guide</h1>
        <p className="text-slate-600 mb-8">Getting started with ExpressSub</p>
        
        {steps.map((s, index) => {
          const Icon = s.icon;
          return (
            <div key={s.id} className="mb-8">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-8 w-8 bg-emerald-100 rounded-full flex items-center justify-center">
                  <Icon className="h-4 w-4 text-emerald-600" />
                </div>
                <h2 className="text-lg font-bold">{s.title}</h2>
              </div>
              <p className="text-slate-500 mb-3">{s.subtitle}</p>
              <div className="pl-11">{s.content}</div>
            </div>
          );
        })}
      </div>

      {/* Interactive View */}
      <Card className="w-full max-w-xl">
        <CardContent className="p-0">
          {/* Header */}
          <div className="p-6 border-b border-slate-200">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <BrandLogo variant="black" size="md" />
              </div>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={handleDownloadPDF} disabled={isExporting} data-testid="download-admin-onboarding-pdf">
                  {isExporting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />}
                  PDF
                </Button>
                <Link to="/guides/admin">
                  <Button variant="ghost" size="sm">
                    <BookOpen className="h-4 w-4 mr-1" />
                    Full Guide
                  </Button>
                </Link>
              </div>
            </div>
            <Progress value={progress} className="h-2" />
            <p className="text-xs text-slate-500 mt-2">Step {currentStep + 1} of {steps.length}</p>
          </div>

          {/* Content */}
          <div className="p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="h-10 w-10 bg-emerald-100 rounded-full flex items-center justify-center">
                <StepIcon className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-900">{step.title}</h2>
                <p className="text-sm text-slate-500">{step.subtitle}</p>
              </div>
            </div>
            
            <div className="mt-6">
              {step.content}
            </div>
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-slate-200 flex items-center justify-between">
            <div>
              {currentStep > 0 ? (
                <Button variant="ghost" onClick={handlePrev}>
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back
                </Button>
              ) : (
                <Button variant="ghost" onClick={handleSkip} className="text-slate-500">
                  Skip for now
                </Button>
              )}
            </div>
            <Button onClick={handleNext} className="bg-emerald-600 hover:bg-emerald-700">
              {currentStep === steps.length - 1 ? 'Go to Dashboard' : 'Next'}
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
